/**
 * @file     HistoryRail.tsx
 * @purpose  Left rail of AI search conversations for signed-in users, styled
 *           like a chat app history: New search, then titled past searches.
 */
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ReltaConversationSummary } from '../types';

export function HistoryRail({
  conversations,
  activeId,
  signedIn,
  onNew,
  onOpen,
  onDelete,
}: {
  conversations: ReltaConversationSummary[];
  activeId: string | null;
  signedIn: boolean;
  onNew: () => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  // Two-step delete: first tap arms, second confirms.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      <ButtonBase
        onClick={onNew}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          color: theme.semanticColors.textPrimary,
          fontSize: 13,
          fontWeight: 600,
          mb: 1,
        }}
      >
        {/* Inline SVG instead of pulling in @mui/icons-material. */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
          <path d="M12 5v14M5 12h14" />
        </svg>
        {t('aiChat.newSearch', 'New search')}
      </ButtonBase>

      {!signedIn && (
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: theme.semanticColors.textSecondary, px: 1.5, py: 1 }}>
          {t('aiChat.railSignedOut', 'Your searches save here once you have an account.')}
        </Typography>
      )}

      {signedIn && conversations.length === 0 && (
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: theme.semanticColors.textMuted, px: 1.5, py: 1 }}>
          {t('aiChat.railEmpty', 'No past searches yet.')}
        </Typography>
      )}

      {conversations.map((c) => (
        <Box
          key={c.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            backgroundColor: c.id === activeId ? theme.semanticColors.surfaceCard : 'transparent',
            '&:hover .relta-del': { opacity: 1 },
          }}
        >
          <ButtonBase
            onClick={() => onOpen(c.id)}
            sx={{
              flex: 1,
              justifyContent: 'flex-start',
              textAlign: 'left',
              px: 1.5,
              py: 1,
              borderRadius: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
              {c.has_unread && c.id !== activeId && (
                <Box
                  aria-label={t('aiChat.unread', 'New message')}
                  sx={{ flexShrink: 0, width: 7, height: 7, borderRadius: '50%', backgroundColor: theme.palette.brand.primary }}
                />
              )}
              <Typography
                noWrap
                sx={{
                  fontSize: 13,
                  color: theme.semanticColors.textPrimary,
                  fontWeight: c.has_unread && c.id !== activeId ? 700 : 400,
                  maxWidth: '100%',
                }}
              >
                {c.title || t('aiChat.untitled', 'Home search')}
              </Typography>
            </Box>
          </ButtonBase>
          {confirmId === c.id ? (
            <Typography
              onClick={() => { setConfirmId(null); onDelete(c.id); }}
              sx={{ fontSize: 12, fontWeight: 600, color: theme.semanticColors.statusError, cursor: 'pointer', px: 1, py: 1, whiteSpace: 'nowrap' }}
            >
              {t('aiChat.deleteConfirm', 'Delete?')}
            </Typography>
          ) : (
            <IconButton
              className="relta-del"
              aria-label={t('aiChat.deleteSearch', 'Delete search')}
              onClick={() => setConfirmId(c.id)}
              onBlur={() => setConfirmId(null)}
              sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s', mr: 0.25, p: 1 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );
}
